import React, { useEffect, useRef, useState } from 'react';
import './ArticleDetails.css';

const ArticleDetails = ({ article, onBackToResults, onBackToHome, onRelatedArticleClick }) => {
    const [showRelatedConcepts, setShowRelatedConcepts] = useState(false);
    const [showSaveOptions, setShowSaveOptions] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [relatedArticles, setRelatedArticles] = useState([
        {
            id: 1,
            title: "Targeting Immunosuppressive Myeloid Cells in Pancreatic Cancer",
            authors: "Zhang H, et al.",
            journal: "Nature Reviews Cancer",
            year: "2022",
            abstract: "This study explores novel approaches to target immunosuppressive myeloid cells in pancreatic cancer...",
            doi: "10.1038/s41568-022-00445-6",
            pmid: "35614234"
        },
        {
            id: 2,
            title: "Stromal Reprogramming in Pancreatic Ductal Adenocarcinoma",
            authors: "Brown K, et al.",
            journal: "Cancer Cell",
            year: "2023",
            abstract: "Research on stromal reprogramming strategies for pancreatic ductal adenocarcinoma...",
            doi: "10.1016/j.ccell.2023.01.012",
            pmid: "36723456"
        },
        {
            id: 3,
            title: "ECM Modulation Strategies for Cancer Therapy",
            authors: "Davis L, et al.",
            journal: "Nature Medicine",
            year: "2022",
            abstract: "Novel extracellular matrix modulation strategies for cancer therapy...",
            doi: "10.1038/s41591-022-01876-2",
            pmid: "35817923"
        },
        {
            id: 4,
            title: "Single-Cell Analysis of Pancreatic Cancer Microenvironment",
            authors: "Miller R, et al.",
            journal: "Cell",
            year: "2023",
            abstract: "Single-cell analysis reveals insights into pancreatic cancer microenvironment...",
            doi: "10.1016/j.cell.2023.02.015",
            pmid: "36823467"
        }
    ]);
    const articleRef = useRef(null);
    const saveButtonRef = useRef(null);

    // Scroll to top when component mounts or article changes
    useEffect(() => {
        // Scroll to top with smooth behavior
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });

        // Also ensure the body scrolls to top
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    }, [article]);

    // Handle search functionality
    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            // Navigate to search results with the new query
            onBackToResults();
        }
    };

    // Handle back navigation with scroll to top
    const handleBackToResults = () => {
        onBackToResults();
    };

    const handleBackToHome = () => {
        onBackToHome();
    };

    // Handle related article click
    const handleRelatedArticleClick = (relatedArticle) => {
        // Create a new article object with the related article data
        const newArticle = {
            id: relatedArticle.id,
            title: relatedArticle.title,
            authors: relatedArticle.authors,
            journal: relatedArticle.journal,
            date: relatedArticle.year,
            abstract: relatedArticle.abstract,
            doi: relatedArticle.doi,
            pmid: relatedArticle.pmid,
            // Add missing fields that the article display expects
            volume: "42",
            issue: "5",
            pages: "1234-1245",
            type: "Research Article",
            citations: "45",
            impactFactor: "38.5 (2023)",
            accessType: "Open Access"
        };

        // Navigate to this article's details using the parent function
        onRelatedArticleClick(newArticle);

        // Scroll to top when navigating to new article
        setTimeout(() => {
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth'
            });
        }, 100);
    };

    // Helper function to wrap text
    const wrapText = (ctx, text, maxWidth, fontSize) => {
        const words = text.split(' ');
        const lines = [];
        let currentLine = words[0];

        for (let i = 1; i < words.length; i++) {
            const word = words[i];
            const width = ctx.measureText(currentLine + " " + word).width;
            if (width < maxWidth) {
                currentLine += " " + word;
            } else {
                lines.push(currentLine);
                currentLine = word;
            }
        }
        lines.push(currentLine);
        return lines;
    };

    const saveAsImage = async () => {
        try {
            const element = articleRef.current;
            if (!element) return;

            // Try to use html2canvas if available
            let html2canvas;
            try {
                html2canvas = (await import('html2canvas')).default;
            } catch (error) {
                // Fallback: create a simple image using canvas API
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const width = 800; // Fixed width for better quality
                const height = 1200; // Fixed height for better quality

                canvas.width = width;
                canvas.height = height;

                // Create a better representation of the article
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, width, height);

                // Add border
                ctx.strokeStyle = '#e0e0e0';
                ctx.lineWidth = 2;
                ctx.strokeRect(10, 10, width - 20, height - 20);

                // Title
                ctx.fillStyle = '#1e3c72';
                ctx.font = 'bold 24px Arial';
                ctx.fillText('Article Details', 30, 60);

                // Article title
                ctx.fillStyle = '#333333';
                ctx.font = 'bold 20px Arial';
                const title = article?.title || 'Novel Therapeutic Approaches for Targeting Tumor Microenvironment in Pancreatic Ductal Adenocarcinoma';
                const wrappedTitle = wrapText(ctx, title, width - 60, 20);
                let yPos = 100;
                wrappedTitle.forEach(line => {
                    ctx.fillText(line, 30, yPos);
                    yPos += 25;
                });

                // Authors
                yPos += 20;
                ctx.fillStyle = '#666666';
                ctx.font = '16px Arial';
                ctx.fillText('Authors:', 30, yPos);
                yPos += 25;
                ctx.fillText(article?.authors || 'Johnson A, Smith B, Williams C, Garcia D, Thompson E, Lee F, Wilson G', 30, yPos);

                // Journal info
                yPos += 30;
                ctx.fillStyle = '#888888';
                ctx.font = '14px Arial';
                ctx.fillText('Journal:', 30, yPos);
                yPos += 20;
                ctx.fillText(`${article?.journal || 'Journal of Clinical Oncology'} • ${article?.date || 'March 2023'}`, 30, yPos);

                // Abstract
                yPos += 40;
                ctx.fillStyle = '#333333';
                ctx.font = 'bold 18px Arial';
                ctx.fillText('Abstract:', 30, yPos);
                yPos += 25;
                ctx.fillStyle = '#555555';
                ctx.font = '14px Arial';

                const abstractText = `Pancreatic ductal adenocarcinoma (PDAC) remains one of the most lethal malignancies, with a 5-year survival rate of less than 10%. The aggressive nature of this disease is largely attributed to the unique tumor microenvironment (TME) characterized by extensive desmoplastic stroma and complex interactions between pancreatic cancer cells, cancer-associated fibroblasts (CAFs), immune cells, and extracellular matrix (ECM) components.`;

                const wrappedAbstract = wrapText(ctx, abstractText, width - 60, 14);
                wrappedAbstract.forEach(line => {
                    if (yPos < height - 50) {
                        ctx.fillText(line, 30, yPos);
                        yPos += 18;
                    }
                });

                // Download the image
                const link = document.createElement('a');
                link.download = `${article?.title?.substring(0, 30) || 'article'}_${new Date().toISOString().split('T')[0]}.png`;
                link.href = canvas.toDataURL();
                link.click();
                alert('Article saved as image successfully!');
                return;
            }

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                width: element.scrollWidth,
                height: element.scrollHeight
            });

            // Convert canvas to blob
            canvas.toBlob((blob) => {
                if (blob) {
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.download = `${article?.title?.substring(0, 30) || 'article'}_${new Date().toISOString().split('T')[0]}.png`;
                    link.href = url;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    alert('Article saved as image successfully!');
                }
            }, 'image/png');
        } catch (error) {
            console.error('Error saving as image:', error);
            alert('Failed to save as image. Please try again.');
        }
        setShowSaveOptions(false);
    };

    const saveAsPDF = async () => {
        try {
            const element = articleRef.current;
            if (!element) return;

            // Try to use jsPDF if available
            let jsPDF;
            try {
                jsPDF = (await import('jspdf')).default;
            } catch (error) {
                // Fallback: create a simple text-based PDF representation
                alert('PDF generation requires jspdf library. Please install it with: npm install jspdf');
                return;
            }

            // Use html2canvas for PDF generation
            let html2canvas;
            try {
                html2canvas = (await import('html2canvas')).default;
            } catch (error) {
                alert('PDF generation requires html2canvas library. Please install it with: npm install html2canvas');
                return;
            }

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                width: element.scrollWidth,
                height: element.scrollHeight
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');

            const imgWidth = 210; // A4 width in mm
            const pageHeight = 295; // A4 height in mm
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;

            let position = 0;

            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            pdf.save(`${article?.title?.substring(0, 30) || 'article'}_${new Date().toISOString().split('T')[0]}.pdf`);
            alert('Article saved as PDF successfully!');
        } catch (error) {
            console.error('Error saving as PDF:', error);
            alert('Failed to save as PDF. Please try again.');
        }
        setShowSaveOptions(false);
    };

    const handleSaveArticle = () => {
        setShowSaveOptions(!showSaveOptions);
    };

    const handleExportReference = () => {
        // Create a citation in different formats
        const citation = `Authors: ${article?.authors || 'Johnson A, et al.'}\nTitle: ${article?.title || 'Novel Therapeutic Approaches...'}\nJournal: ${article?.journal || 'Journal of Clinical Oncology'}\nYear: ${article?.date || '2023'}\nDOI: ${article?.doi || '10.1200/JCO.2022.41.8.1423'}`;

        // Copy to clipboard
        navigator.clipboard.writeText(citation).then(() => {
            alert('Citation copied to clipboard!');
        }).catch(() => {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = citation;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            alert('Citation copied to clipboard!');
        });
    };

    const handleBookmark = () => {
        // Save to localStorage
        const bookmarks = JSON.parse(localStorage.getItem('medsearch_bookmarks') || '[]');
        const bookmark = {
            id: article?.id || Date.now(),
            title: article?.title || 'Novel Therapeutic Approaches...',
            authors: article?.authors || 'Johnson A, et al.',
            journal: article?.journal || 'Journal of Clinical Oncology',
            date: article?.date || '2023',
            timestamp: new Date().toISOString()
        };

        // Check if already bookmarked
        const existingIndex = bookmarks.findIndex(b => b.id === bookmark.id);
        if (existingIndex >= 0) {
            bookmarks.splice(existingIndex, 1);
            localStorage.setItem('medsearch_bookmarks', JSON.stringify(bookmarks));
            alert('Article removed from bookmarks!');
        } else {
            bookmarks.push(bookmark);
            localStorage.setItem('medsearch_bookmarks', JSON.stringify(bookmarks));
            alert('Article bookmarked successfully!');
        }
    };

    // Close save options when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (saveButtonRef.current && !saveButtonRef.current.contains(event.target)) {
                setShowSaveOptions(false);
            }
        };

        if (showSaveOptions) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSaveOptions]);

    return (
        <div className="article-details">
            {/* Logo Section Above Navbar */}
            <div className="logo-section">
                <div className="logo-container">
                    <div className="logo">
                        <i className="fas fa-search"></i>
                        <span>MedSearch</span>
                    </div>
                </div>
            </div>

            {/* Header */}
            <header className="article-header">
                <div className="header-content">
                    <div className="header-left">
                        <div className="breadcrumbs">
                            <span onClick={onBackToHome} className="breadcrumb-link">Home</span>
                            <span className="breadcrumb-separator"> › </span>
                            <span onClick={handleBackToResults} className="breadcrumb-link">Search Results</span>
                            <span className="breadcrumb-separator"> › </span>
                            <span className="breadcrumb-current">Article Details</span>
                        </div>
                    </div>
                    <div className="search-section">
                        <form onSubmit={handleSearch} className="search-bar">
                            <input
                                type="text"
                                placeholder="Search medical literature..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <button type="submit" className="search-button">
                                <i className="fas fa-search"></i>
                            </button>
                        </form>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="article-main" ref={articleRef}>
                <div className="article-container">
                    {/* Article Title and Authors */}
                    <div className="article-header-section">
                        <h1 className="article-title">
                            {article?.title || "Novel Therapeutic Approaches for Targeting Tumor Microenvironment in Pancreatic Ductal Adenocarcinoma"}
                        </h1>
                        <p className="article-authors">
                            {article?.authors || "Johnson A, Smith B, Williams C, Garcia D, Thompson E, Lee F, Wilson G"}
                        </p>
                        <p className="article-journal">
                            {article?.journal || "Journal of Clinical Oncology"} • {article?.date || "March 2023"} • Vol {article?.volume || "41"}, Issue {article?.issue || "8"} • pp. {article?.pages || "1423-1435"}
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="action-buttons">
                        <div className="save-button-container" ref={saveButtonRef}>
                            <button className="action-btn save-btn" onClick={handleSaveArticle}>
                                <i className="fas fa-save"></i>
                                Save Article
                            </button>
                            {showSaveOptions && (
                                <div className="save-options">
                                    <button className="save-option" onClick={saveAsImage}>
                                        <i className="fas fa-image"></i>
                                        Save as Image
                                    </button>
                                    <button className="save-option" onClick={saveAsPDF}>
                                        <i className="fas fa-file-pdf"></i>
                                        Save as PDF
                                    </button>
                                </div>
                            )}
                        </div>
                        <button className="action-btn export-btn" onClick={handleExportReference}>
                            <i className="fas fa-cloud-download-alt"></i>
                            Export to Reference Manager
                        </button>
                        <button className="action-btn bookmark-btn" onClick={handleBookmark}>
                            <i className="fas fa-bookmark"></i>
                            Bookmark
                        </button>
                    </div>

                    {/* Abstract Section */}
                    <section className="abstract-section">
                        <h2 className="section-title">Abstract</h2>
                        <div className="abstract-content">
                            <p>
                                Pancreatic ductal adenocarcinoma (PDAC) remains one of the most lethal malignancies,
                                with a 5-year survival rate of less than 10%. The aggressive nature of this disease
                                is largely attributed to the unique <span className="highlight-term">tumor microenvironment (TME)</span> characterized by
                                extensive <span className="highlight-term">desmoplastic stroma</span> and complex interactions between
                                <span className="highlight-term">pancreatic cancer cells</span>, <span className="highlight-term">cancer-associated fibroblasts (CAFs)</span>,
                                <span className="highlight-term">immune cells</span>, and <span className="highlight-term">extracellular matrix (ECM)</span> components.
                            </p>
                            <p>
                                Recent advances in understanding the TME have revealed novel therapeutic opportunities
                                through <span className="highlight-term">stromal reprogramming</span> and <span className="highlight-term">immune checkpoint inhibition</span>.
                                This review discusses emerging strategies targeting the TME, including
                                <span className="highlight-term">ECM modulation</span>, <span className="highlight-term">patient stratification</span> approaches,
                                and the integration of <span className="highlight-term">3D organoid models</span> and
                                <span className="highlight-term">single-cell RNA sequencing</span> for preclinical validation.
                            </p>
                            <p>
                                Clinical trials evaluating <span className="highlight-term">stromal-targeting agents</span> in combination
                                with conventional chemotherapy and <span className="highlight-term">immunotherapies</span> show promising
                                results, particularly in patient subsets with specific TME characteristics.
                                Future directions include the development of personalized TME-targeting strategies
                                and the identification of predictive biomarkers for treatment response.
                            </p>
                        </div>
                        <button
                            className="related-concepts-btn"
                            onClick={() => setShowRelatedConcepts(!showRelatedConcepts)}
                        >
                            <i className="fas fa-sitemap"></i>
                            Show Related Concepts
                        </button>
                    </section>

                    {/* Article Metadata */}
                    <section className="metadata-section">
                        <h2 className="section-title">Article Metadata</h2>
                        <div className="metadata-grid">
                            <div className="metadata-column">
                                <div className="metadata-item">
                                    <span className="metadata-label">DOI:</span>
                                    <span className="metadata-value">{article?.doi || "10.1200/JCO.2022.41.8.1423"}</span>
                                </div>
                                <div className="metadata-item">
                                    <span className="metadata-label">PMID:</span>
                                    <span className="metadata-value">{article?.pmid || "36789245"}</span>
                                </div>
                                <div className="metadata-item">
                                    <span className="metadata-label">Publication Type:</span>
                                    <span className="metadata-value">{article?.type || "Review Article"}</span>
                                </div>
                            </div>
                            <div className="metadata-column">
                                <div className="metadata-item">
                                    <span className="metadata-label">Citation Count:</span>
                                    <span className="metadata-value">{article?.citations || "87"}</span>
                                </div>
                                <div className="metadata-item">
                                    <span className="metadata-label">Journal Impact Factor:</span>
                                    <span className="metadata-value">{article?.impactFactor || "44.544 (2022)"}</span>
                                </div>
                                <div className="metadata-item">
                                    <span className="metadata-label">Access Type:</span>
                                    <span className="metadata-value">{article?.accessType || "Open Access"}</span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Related Articles */}
                    <section className="related-articles-section">
                        <h2 className="section-title">You may also be interested in:</h2>
                        <div className="related-articles-grid">
                            {relatedArticles.map((relatedArticle) => (
                                <div
                                    key={relatedArticle.id}
                                    className="related-article-card"
                                    onClick={() => handleRelatedArticleClick(relatedArticle)}
                                >
                                    <h3 className="related-article-title">{relatedArticle.title}</h3>
                                    <p className="related-article-authors">{relatedArticle.authors}</p>
                                    <p className="related-article-journal">
                                        {relatedArticle.journal} • {relatedArticle.year}
                                    </p>
                                    <div className="related-article-abstract">
                                        {relatedArticle.abstract.substring(0, 150)}...
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            </main>

            {/* Footer */}
            <footer className="article-footer">
                <div className="footer-content">
                    <button className="back-btn" onClick={handleBackToResults}>
                        <i className="fas fa-arrow-left"></i>
                        ← Back to search results
                    </button>
                    <div className="footer-links">
                        <a href="#help">Help</a>
                        <a href="#privacy">Privacy Policy</a>
                        <a href="#terms">Terms of Use</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ArticleDetails;
